/**
 * Explorer API client.
 * Centralizes fetch status handling, Googlebot query forwarding, and route URLs.
 */

const ExplorerApiConfig = window.ExplorerConfig || {};
const ExplorerSearch = window.DataExplorerSearch;

function explorerApiUrl(path) {
  if (ExplorerApiConfig.IS_GOOGLEBOT && !path.includes('__goog')) {
    return path + (path.includes('?') ? '&' : '?') + '__goog=true';
  }
  return path;
}

function explorerAuthRedirectedToLogin(response) {
  if (!response?.redirected || !response.url) return false;
  try {
    const url = new URL(response.url, window.location.origin);
    return url.pathname === '/auth/login';
  } catch (_e) {
    return false;
  }
}

function explorerFetchJson(path, options) {
  const fetchOptions = options?.fetchOptions || options;
  return fetch(path, fetchOptions).then(async (response) => {
    if (options?.redirectAuthToRegister && explorerAuthRedirectedToLogin(response)) {
      window.location.assign('/auth/register');
      return {
        success: false,
        auth_redirect: true,
        error: 'Authentication required',
        http_status: response.status,
      };
    }

    const data = await response.json().catch(() => ({}));
    data.http_status = response.status;
    if (!response.ok && !data.error) data.error = `Request failed with status ${response.status}`;
    return data;
  });
}

async function explorerFetchStreamingJson(path, { onChunk } = {}) {
  const response = await fetch(path);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    data.http_status = response.status;
    if (!data.error) data.error = `Request failed with status ${response.status}`;
    return data;
  }

  if (!response.body || !window.TextDecoder) {
    const data = await response.json().catch(() => ({}));
    data.http_status = response.status;
    return data;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let text = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    text += decoder.decode(value, { stream: true });
    if (onChunk) onChunk(text.length);
  }
  text += decoder.decode();

  const data = JSON.parse(text);
  data.http_status = response.status;
  return data;
}

window.ExplorerApi = {
  searchCompanies(q) {
    return ExplorerSearch.searchCompanies(q).then(results => ({ results }));
  },
  getRecentlyViewed() {
    return explorerFetchJson(
      ExplorerApiConfig.IS_DASHBOARD
        ? '/dashboard/explorer/api/recently-viewed'
        : '/api/explorer/recently-viewed'
    );
  },
  getDiscoverPeople(excludeSlug) {
    const qs = excludeSlug ? `?exclude_slug=${encodeURIComponent(excludeSlug)}` : '';
    return explorerFetchJson(`/dashboard/explorer/api/discover-people${qs}`);
  },
  recordView(slug) {
    fetch(`/api/explorer/view/${encodeURIComponent(slug)}`, { method: 'POST' });
  },
  getCompanySummary(slug) {
    return explorerFetchJson(`/api/explorer/company/${encodeURIComponent(slug)}/summary`);
  },
  getFullProfile(slug) {
    return explorerFetchJson(`/api/explorer/company/${encodeURIComponent(slug)}/full-profile?include_employee_count=true`);
  },
  getTimeline(slug, mode) {
    return explorerFetchJson(explorerApiUrl(`/api/explorer/company/${encodeURIComponent(slug)}/timeline?mode=${mode}`));
  },
  getAlsoViewed(slug) {
    return explorerFetchJson(`/api/explorer/company/${encodeURIComponent(slug)}/also-viewed`);
  },
  getCompanyIndex(page, limit) {
    return explorerFetchJson(`/api/explorer/companies?page=${page}${limit ? '&limit=' + limit : ''}`);
  },
  getPreviewCustomers(slug) {
    return explorerFetchJson(explorerApiUrl(`/api/explorer/company/${encodeURIComponent(slug)}/preview-customers`));
  },
  getPreviewCompetitors(slug) {
    return explorerFetchJson(explorerApiUrl(`/api/explorer/company/${encodeURIComponent(slug)}/preview-competitors`));
  },
  getPreviewFunding(slug) {
    return explorerFetchJson(explorerApiUrl(`/api/explorer/company/${encodeURIComponent(slug)}/preview-funding`));
  },
  loadCustomers(domain) {
    return explorerFetchJson(`/dashboard/customers/api/company/${encodeURIComponent(domain)}`);
  },
  loadCompetitors(domain) {
    return explorerFetchJson(`/dashboard/explorer/api/competitors/${encodeURIComponent(domain)}`);
  },
  getFunding(domain) {
    return explorerFetchJson(`/dashboard/explorer/api/funding/${encodeURIComponent(domain)}`);
  },
  getCreditBalance() {
    return explorerFetchJson('/dashboard/customers/api/credit-balance');
  },
  checkSavedCustomers(domain) {
    return explorerFetchJson(`/dashboard/customers/api/saved-lists/by-domain/${encodeURIComponent(domain)}`);
  },
  checkSavedCompetitors(domain) {
    return explorerFetchJson(`/dashboard/customers/api/saved-competitor-lists/by-domain/${encodeURIComponent(domain)}`);
  },
  checkSavedProfile(domain) {
    return explorerFetchJson(`/api/explorer/saved-company-profiles/by-domain/${encodeURIComponent(domain)}`);
  },
  getCompanyProfileProspectorSheets(domain) {
    return explorerFetchJson(`/dashboard/explorer/api/company/${encodeURIComponent(domain)}/prospector-sheets`);
  },
  lookupPerson(payload) {
    return explorerFetchJson('/dashboard/explorer/api/person/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
  },
  getPersonSummary(slug) {
    return explorerFetchJson(`/dashboard/explorer/api/person/${encodeURIComponent(slug)}/summary`);
  },
  getPersonFullProfile(slug) {
    return explorerFetchJson(`/dashboard/explorer/api/person/${encodeURIComponent(slug)}/full-profile`);
  },
  checkSavedPersonProfile(slug) {
    return explorerFetchJson(`/dashboard/explorer/api/saved-person-profiles/by-slug/${encodeURIComponent(slug)}`);
  },
  getSimilarPeopleStatus(slug) {
    return explorerFetchJson(`/dashboard/explorer/api/person/${encodeURIComponent(slug)}/similar/status`);
  },
  getSimilarPeople(slug, onChunk) {
    return explorerFetchStreamingJson(`/dashboard/explorer/api/person/${encodeURIComponent(slug)}/similar`, { onChunk });
  },
  openSimilarPeopleProspector(slug) {
    return explorerFetchJson(`/dashboard/explorer/api/person/${encodeURIComponent(slug)}/similar/prospector`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  },
  getProspectorSheet(sheetId) {
    return explorerFetchJson(`/dashboard/api/prospector/sheets/${encodeURIComponent(sheetId)}`);
  },
  openCompanyProfileProspector(action, domain, mode = 'preview') {
    return explorerFetchJson(`/dashboard/explorer/api/company/${encodeURIComponent(domain)}/prospector/${encodeURIComponent(action)}`, {
      redirectAuthToRegister: !ExplorerApiConfig.IS_DASHBOARD,
      fetchOptions: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      },
    });
  },
  openProspector(template, domain) {
    return explorerFetchJson(`/dashboard/api/prospector/templates/${encodeURIComponent(template)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    });
  },
};

window.ExplorerFetch = {
  apiUrl: explorerApiUrl,
  fetchJson: explorerFetchJson,
  fetchStreamingJson: explorerFetchStreamingJson,
};
